'use client'

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { Dialog, DialogPanel, Transition, TransitionChild, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { ChevronDown, SlidersHorizontal, X, Loader2, PackageOpen, Search } from 'lucide-react'
import ProductCard from './ProductCard'
import { useInfiniteProducts } from '@/hooks/use-infinite-products'
import { useCart } from '@/lib/commerce/hooks'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import { Button } from '@/components/elements/Button'
import { formatPrice, getEffectivePrice, getStrikethroughPrice, getDisplayDiscounts, hasLoyaltyTierBenefits, isLoyaltyActive } from '@/utils/util'
import { SORT_OPTIONS, SPECIAL_CATEGORY_FILTERS, SPECIAL_CATEGORY_LABELS, type SpecialCategoryFilter, findCategoryById, getCategoryDescendantIds, buildInitialProductFilters } from '@/utils/product'
import type { Session } from '@/utils/types'

type ProductListProps = {
  vendor: any
  categoryList: any[]
  brandList: any[]
  wishlist?: any
  session?: Session | null
}

const PRICE_MIN = 0
const PRICE_MAX = 2000000

const SALE_BUCKETS = [
  { id: '50', label: '50%-аас дээш', min: 50 },
  { id: '25', label: '25%-аас дээш', min: 25 },
  { id: '10', label: '10%-аас дээш', min: 10 },
  { id: '5', label: '5%-аас дээш', min: 5 },
]

const RATING_BUCKETS = [
  { id: '4.5', label: '4.5+', min: 4.5 },
  { id: '4.0', label: '4.0+', min: 4.0 },
  { id: '3.5', label: '3.5+', min: 3.5 },
]

function Section({ title, defaultOpen = true, children }: { title: string, defaultOpen?: boolean, children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border-b border-foreground/10">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-4 text-left text-foreground cursor-pointer"
      >
        <span className="text-[15px] font-semibold">{title}</span>
        <ChevronDown className={`size-4 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="pb-5">{children}</div>}
    </div>
  )
}

function Check({ checked, onChange, label, count }: { checked: boolean, onChange: (v: boolean) => void, label: string, count?: number }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className="w-full flex items-center justify-between py-2 group text-left cursor-pointer"
    >
      <span className="inline-flex items-center gap-3 min-w-0">
        <span
          className={`shrink-0 w-5 h-5 rounded-[6px] border inline-flex items-center justify-center transition-colors ${checked ? 'bg-foreground border-foreground text-white' : 'border-foreground/20 bg-white group-hover:border-foreground/40'}`}
        >
          {checked && (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          )}
        </span>
        <span className="text-[14px] text-foreground truncate">{label}</span>
      </span>
      {count != null && (
        <span className="text-[13px] text-foreground/55 tabular-nums shrink-0 pl-3">{count.toLocaleString('en-US')}</span>
      )}
    </button>
  )
}

function PriceHistogram({ bins, lo, hi }: { bins: { x: number, count: number }[], lo: number, hi: number }) {
  const maxCount = Math.max(1, ...bins.map(b => b.count))
  return (
    <div className="h-14 flex items-end gap-[2px] px-[1px]">
      {bins.map((b, i) => {
        const inRange = b.x >= lo && b.x <= hi
        const h = Math.max(8, Math.round((b.count / maxCount) * 100))
        return (
          <div
            key={i}
            className={`flex-1 rounded-[2px] transition-colors ${inRange ? 'bg-foreground' : 'bg-foreground/15'}`}
            style={{ height: `${h}%` }}
          />
        )
      })}
    </div>
  )
}

function DualRange({ min, max, step, lo, hi, onChange }: { min: number, max: number, step: number, lo: number, hi: number, onChange: (lo: number, hi: number) => void }) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState<'lo' | 'hi' | null>(null)

  const pct = (v: number) => ((v - min) / (max - min)) * 100

  const setFromClientX = useCallback((clientX: number, which: 'lo' | 'hi') => {
    const el = trackRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    let p = (clientX - rect.left) / rect.width
    p = Math.max(0, Math.min(1, p))
    let v = Math.round((min + p * (max - min)) / step) * step
    v = Math.max(min, Math.min(max, v))
    if (which === 'lo') onChange(Math.min(v, hi - step), hi)
    else onChange(lo, Math.max(v, lo + step))
  }, [min, max, step, lo, hi, onChange])

  useEffect(() => {
    if (!dragging) return
    const move = (e: MouseEvent | TouchEvent) => {
      const x = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX
      setFromClientX(x, dragging)
    }
    const up = () => setDragging(null)
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
    window.addEventListener('touchmove', move)
    window.addEventListener('touchend', up)
    return () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
      window.removeEventListener('touchmove', move)
      window.removeEventListener('touchend', up)
    }
  }, [dragging, setFromClientX])

  const loP = pct(lo), hiP = pct(hi)

  return (
    <div className="relative h-6 select-none mx-2.25">
      <div ref={trackRef} className="absolute inset-0">
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[2px] bg-foreground/15 rounded-full" />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-[2px] rounded-full bg-foreground"
          style={{ left: `${loP}%`, right: `${100 - hiP}%` }}
        />
        {([
          { which: 'lo' as const, p: loP },
          { which: 'hi' as const, p: hiP },
        ]).map(({ which, p }) => (
          <button
            key={which}
            aria-label={which === 'lo' ? 'Доод үнэ' : 'Дээд үнэ'}
            onMouseDown={(e) => { e.preventDefault(); setDragging(which) }}
            onTouchStart={() => setDragging(which)}
            className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4.5 h-4.5 rounded-full bg-white border border-foreground/15 shadow-sm cursor-grab active:cursor-grabbing transition-shadow ${dragging === which ? 'ring-4 ring-foreground/10' : ''}`}
            style={{ left: `${p}%` }}
          />
        ))}
      </div>
    </div>
  )
}

function PriceInput({ value, onChange, onCommit }: { value: string, onChange: (v: string) => void, onCommit: (v: string) => void }) {
  return (
    <div className="relative flex-1 min-w-0">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/55 text-[13px]">₮</span>
      <input
        value={Number(String(value).replace(/[^\d]/g, '')).toLocaleString('en-US')}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => onCommit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
        inputMode="numeric"
        className="w-full min-w-0 h-10 pl-7 pr-2 rounded-md border border-foreground/15 bg-white text-sm tabular-nums focus:outline-none focus:border-foreground/40"
      />
    </div>
  )
}

function PriceFilter({ bins, lo, hi, onChange }: { bins: { x: number, count: number }[], lo: number, hi: number, onChange: (lo: number, hi: number) => void }) {
  const [loText, setLoText] = useState(String(lo))
  const [hiText, setHiText] = useState(String(hi))
  useEffect(() => { setLoText(String(lo)) }, [lo])
  useEffect(() => { setHiText(String(hi)) }, [hi])

  const commit = (which: 'lo' | 'hi', raw: string) => {
    const n = Number(String(raw).replace(/[^\d]/g, ''))
    if (Number.isNaN(n)) return
    if (which === 'lo') onChange(Math.max(PRICE_MIN, Math.min(n, hi - 1000)), hi)
    else onChange(lo, Math.min(PRICE_MAX, Math.max(n, lo + 1000)))
  }

  return (
    <div>
      <PriceHistogram bins={bins} lo={lo} hi={hi} />
      <div className="mt-1">
        <DualRange min={PRICE_MIN} max={PRICE_MAX} step={1000} lo={lo} hi={hi} onChange={onChange} />
      </div>
      <div className="mt-4 flex items-center gap-1.5">
        <PriceInput value={loText} onChange={setLoText} onCommit={(v) => commit('lo', v)} />
        <span className="text-foreground/55 shrink-0">—</span>
        <PriceInput value={hiText} onChange={setHiText} onCommit={(v) => commit('hi', v)} />
      </div>
    </div>
  )
}

function makeBins(prices: number[], min: number, max: number, n = 28) {
  const w = (max - min) / n
  const bins = Array.from({ length: n }, (_, i) => ({ x: min + (i + 0.5) * w, count: 0 }))
  prices.forEach(p => {
    const i = Math.min(n - 1, Math.max(0, Math.floor((p - min) / w)))
    bins[i].count++
  })
  return bins
}

function buildPriceBuckets(prices: number[]) {
  const lt = (n: number) => prices.filter(p => p < n).length
  const between = (a: number, b: number) => prices.filter(p => p >= a && p < b).length
  const gte = (n: number) => prices.filter(p => p >= n).length
  return [
    { id: '0-100k', label: '₮100,000 хүртэл', lo: 0, hi: 100000, count: lt(100000) },
    { id: '100-500k', label: '₮100,000 — ₮500,000', lo: 100000, hi: 500000, count: between(100000, 500000) },
    { id: '500k+', label: '₮500,000-аас дээш', lo: 500000, hi: PRICE_MAX, count: gte(500000) },
  ]
}

function BrandList({ brands, selected, onToggle }: { brands: any[], selected: string[], onToggle: (id: string) => void }) {
  const [q, setQ] = useState('')
  const [showAll, setShowAll] = useState(false)
  const filtered = useMemo(
    () => brands.filter(b => b.name?.toLowerCase().includes(q.toLowerCase())),
    [brands, q],
  )
  const visible = showAll ? filtered : filtered.slice(0, 6)
  return (
    <div>
      <div className="relative mb-2">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/55"><Search className="size-4" /></span>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Брэнд хайх"
          className="w-full h-10 pl-9 pr-3 rounded-md border border-foreground/15 bg-white text-sm focus:outline-none focus:border-foreground/40"
        />
      </div>
      <div>
        {visible.map((b: any) => (
          <Check
            key={b.id}
            label={b.name}
            checked={selected.includes(b.id)}
            onChange={() => onToggle(b.id)}
          />
        ))}
        {filtered.length === 0 && (
          <div className="py-4 text-center text-[13px] text-foreground/55">Илэрц олдсонгүй</div>
        )}
      </div>
      {filtered.length > 6 && (
        <button
          onClick={() => setShowAll(s => !s)}
          className="mt-1 text-[14px] underline text-foreground hover:opacity-70 cursor-pointer"
        >
          {showAll ? 'Цөөн харах' : `Бүгдийг харах (${filtered.length})`}
        </button>
      )}
    </div>
  )
}

function getProductPrice(p: any, isLoggedIn: boolean) {
  const variant = p?.variants?.[0]
  if (!variant) return 0
  const discounts = getDisplayDiscounts(p, isLoggedIn, variant?.id)
  const eff = getEffectivePrice(variant, discounts)
  return eff ?? variant?.price ?? 0
}

function getProductDiscountPct(p: any, isLoggedIn: boolean) {
  const variant = p?.variants?.[0]
  if (!variant) return 0
  const discounts = getDisplayDiscounts(p, isLoggedIn, variant?.id)
  const eff = getEffectivePrice(variant, discounts)
  const strike = getStrikethroughPrice(variant, discounts)
  if (eff == null || !strike || strike <= eff) return 0
  return Math.round((1 - eff / strike) * 100)
}

export default function ProductList({ vendor, categoryList, brandList, session }: ProductListProps) {
  const router = useRouter()
  const pathname = usePathname() || ''
  const searchParams = useSearchParams()
  const { loyaltySummary } = useCart()
  const showLoyaltyFilter = isLoyaltyActive(vendor) && hasLoyaltyTierBenefits(loyaltySummary)
  const categories = categoryList || []
  const brands = brandList || []
  const isLoggedIn = !!session?.user?.id

  const [openFilters, setOpenFilters] = useState(false)
  const [sort, setSort] = useState<string>(searchParams?.get('sort') || SORT_OPTIONS[0]?.value)

  const initialBrandIds = useMemo(() => searchParams?.get('brand')?.split(',').filter(Boolean) || [], [searchParams])
  const [selectedBrands, setSelectedBrands] = useState<string[]>(initialBrandIds)

  const [lo, setLo] = useState<number>(Number(searchParams?.get('priceMin')) || PRICE_MIN)
  const [hi, setHi] = useState<number>(Number(searchParams?.get('priceMax')) || PRICE_MAX)
  const [priceBucket, setPriceBucket] = useState<string | null>(null)
  const [saleMin, setSaleMin] = useState<number | null>(null)
  const [ratingMin, setRatingMin] = useState<number | null>(null)
  const [freeShip, setFreeShip] = useState<boolean>(false)

  const categoryParam = searchParams?.get('category') || null
  const filterParam = searchParams?.get('filter') || null
  const activeCategoryId = filterParam || categoryParam

  const getInitialFilters = useCallback(() => buildInitialProductFilters(searchParams, categories), [searchParams, categories])

  const { products, isLoading, isLoadingMore, metadata, loadMore, hasMore, updateFilters, pendingCount } = useInfiniteProducts({
    vendorId: vendor?.id,
    limit: 20,
    initialFilters: getInitialFilters(),
    session,
  })

  const loadMoreRef = useRef<HTMLDivElement>(null)
  useInfiniteScroll({
    targetRef: loadMoreRef,
    onLoadMore: loadMore,
    enabled: hasMore && !isLoading && !isLoadingMore,
  })

  const setCategoryParams = (id: string | null) => {
    const params = new URLSearchParams(searchParams?.toString() || '')
    if (id == null) {
      params.delete('category')
      params.delete('filter')
      updateFilters({ category: null, filter: null })
    } else if (SPECIAL_CATEGORY_FILTERS.includes(id as SpecialCategoryFilter)) {
      params.delete('category')
      params.set('filter', id)
      updateFilters({ category: null, filter: id })
    } else {
      const found = findCategoryById(categories, id)
      const ids = found ? getCategoryDescendantIds(found) : [id]
      params.set('category', id)
      params.delete('filter')
      updateFilters({ category: ids.join(','), filter: null })
    }
    router.replace(`${pathname}?${params.toString()}`, { scroll: false })
  }

  const toggleBrand = (id: string) => {
    const next = selectedBrands.includes(id) ? selectedBrands.filter(b => b !== id) : [...selectedBrands, id]
    setSelectedBrands(next)
    const params = new URLSearchParams(searchParams?.toString() || '')
    if (next.length > 0) params.set('brand', next.join(','))
    else params.delete('brand')
    updateFilters({ brand: next.length > 0 ? next.join(',') : null })
    router.replace(`${pathname}?${params.toString()}`, { scroll: false })
  }

  const onSortChange = (value: string) => {
    setSort(value)
    const params = new URLSearchParams(searchParams?.toString() || '')
    params.set('sort', value)
    updateFilters({ sort: value })
    router.replace(`${pathname}?${params.toString()}`, { scroll: false })
  }

  const applyPrice = (newLo: number, newHi: number) => {
    setLo(newLo)
    setHi(newHi)
    const params = new URLSearchParams(searchParams?.toString() || '')
    if (newLo > PRICE_MIN) params.set('priceMin', String(newLo)); else params.delete('priceMin')
    if (newHi < PRICE_MAX) params.set('priceMax', String(newHi)); else params.delete('priceMax')
    router.replace(`${pathname}?${params.toString()}`, { scroll: false })
  }

  const onPriceChange = (newLo: number, newHi: number) => {
    setPriceBucket(null)
    applyPrice(newLo, newHi)
  }

  const setPriceBucketHandler = (bk: { id: string, lo: number, hi: number }) => {
    if (priceBucket === bk.id) {
      setPriceBucket(null)
      applyPrice(PRICE_MIN, PRICE_MAX)
    } else {
      setPriceBucket(bk.id)
      applyPrice(bk.lo, bk.hi)
    }
  }

  const clearAll = () => {
    setSelectedBrands([])
    setSort(SORT_OPTIONS[0]?.value)
    setLo(PRICE_MIN); setHi(PRICE_MAX); setPriceBucket(null)
    setSaleMin(null); setRatingMin(null); setFreeShip(false)
    updateFilters({ category: null, filter: null, brand: null, sort: SORT_OPTIONS[0]?.value })
    router.replace(pathname, { scroll: false })
  }

  const prices = useMemo(() => products.map((p: any) => getProductPrice(p, isLoggedIn)), [products, isLoggedIn])
  const bins = useMemo(() => makeBins(prices, PRICE_MIN, PRICE_MAX, 32), [prices])
  const priceBuckets = useMemo(() => buildPriceBuckets(prices), [prices])

  const displayedProducts = useMemo(() => {
    let xs = products.filter((p: any) => {
      const price = getProductPrice(p, isLoggedIn)
      if (price < lo || price > hi) return false
      return true
    })
    if (saleMin != null) xs = xs.filter((p: any) => getProductDiscountPct(p, isLoggedIn) >= saleMin)
    if (ratingMin != null) xs = xs.filter((p: any) => (p.averageRating ?? 0) >= ratingMin)
    if (freeShip) xs = xs.filter((p: any) => p.freeShipping)
    return xs
  }, [products, isLoggedIn, lo, hi, saleMin, ratingMin, freeShip])

  const hasActiveFilters = !!(activeCategoryId || selectedBrands.length > 0 || lo > PRICE_MIN || hi < PRICE_MAX || saleMin != null || ratingMin != null || freeShip)

  const topCategories = useMemo(() => categories.filter((c: any) => c.level === 0), [categories])
  const activeSortLabel = SORT_OPTIONS.find((o: any) => o.value === sort)?.label || SORT_OPTIONS[0]?.label

  const currentCategoryName = useMemo(() => {
    if (!activeCategoryId) return null
    if (SPECIAL_CATEGORY_FILTERS.includes(activeCategoryId as SpecialCategoryFilter)) return SPECIAL_CATEGORY_LABELS[activeCategoryId as SpecialCategoryFilter]
    return findCategoryById(categories, activeCategoryId)?.name || null
  }, [activeCategoryId, categories])

  const chips: { key: string, label: string, clear: () => void }[] = []
  if (lo > PRICE_MIN || hi < PRICE_MAX) {
    chips.push({ key: 'price', label: `${formatPrice(lo)} — ${formatPrice(hi)}`, clear: () => { setLo(PRICE_MIN); setHi(PRICE_MAX); setPriceBucket(null) } })
  }
  if (saleMin != null) chips.push({ key: 'sale', label: `${saleMin}%-аас дээш хямдрал`, clear: () => setSaleMin(null) })
  if (ratingMin != null) chips.push({ key: 'rating', label: `Үнэлгээ ${ratingMin}+`, clear: () => setRatingMin(null) })
  if (freeShip) chips.push({ key: 'ship', label: 'Үнэгүй хүргэлт', clear: () => setFreeShip(false) })
  if (activeCategoryId && currentCategoryName) {
    chips.push({ key: 'cat', label: currentCategoryName, clear: () => setCategoryParams(null) })
  }
  selectedBrands.forEach(brandId => {
    const b = brands.find((br: any) => br.id === brandId)
    chips.push({ key: `brand:${brandId}`, label: b?.name || brandId, clear: () => toggleBrand(brandId) })
  })

  const SidebarContent = (
    <>
      <div className="hidden lg:block text-[28px] font-bold font-gilroy text-foreground mb-2">Шүүлтүүр</div>
      <Section title="Үнэ" defaultOpen>
        <PriceFilter bins={bins} lo={lo} hi={hi} onChange={onPriceChange} />
        <div className="mt-3">
          {priceBuckets.map(b => (
            <Check
              key={b.id}
              label={b.label}
              count={b.count}
              checked={priceBucket === b.id}
              onChange={() => setPriceBucketHandler(b)}
            />
          ))}
        </div>
      </Section>
      <Section title="Хямдрал">
        {SALE_BUCKETS.map(b => {
          const count = products.filter((p: any) => getProductDiscountPct(p, isLoggedIn) >= b.min).length
          return (
            <Check
              key={b.id}
              label={b.label}
              count={count}
              checked={saleMin === b.min}
              onChange={(v) => setSaleMin(v ? b.min : null)}
            />
          )
        })}
      </Section>
      <Section title="Ангилал">
        <div className="space-y-1">
          {SPECIAL_CATEGORY_FILTERS.filter((id: SpecialCategoryFilter) => id !== 'loyalty' || showLoyaltyFilter).map((id: SpecialCategoryFilter) => (
            <Check
              key={id}
              label={SPECIAL_CATEGORY_LABELS[id]}
              checked={activeCategoryId === id}
              onChange={() => setCategoryParams(activeCategoryId === id ? null : id)}
            />
          ))}
        </div>
        {topCategories.length > 0 && (
          <div className="mt-3 pt-3 border-t border-foreground/10 space-y-1">
            {topCategories.map((c: any) => (
              <Check
                key={c.id}
                label={c.name}
                checked={activeCategoryId === c.id}
                onChange={() => setCategoryParams(activeCategoryId === c.id ? null : c.id)}
              />
            ))}
          </div>
        )}
      </Section>
      {brands.length > 0 && (
        <Section title="Брэнд">
          <BrandList brands={brands} selected={selectedBrands} onToggle={toggleBrand} />
        </Section>
      )}
      <Section title="Үнэлгээ">
        {RATING_BUCKETS.map(b => {
          const count = products.filter((p: any) => (p.averageRating ?? 0) >= b.min).length
          return (
            <Check
              key={b.id}
              label={b.label}
              count={count}
              checked={ratingMin === b.min}
              onChange={(v) => setRatingMin(v ? b.min : null)}
            />
          )
        })}
      </Section>
      <Section title="Хүргэлт">
        <Check
          label="Үнэгүй хүргэлт"
          count={products.filter((p: any) => p.freeShipping).length}
          checked={freeShip}
          onChange={setFreeShip}
        />
      </Section>
    </>
  )

  return (
    <section className="bg-cream pt-6 pb-16">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">
        <nav className="text-[13px] text-foreground/55 mb-3">
          <a href={`${pathname?.split('/').slice(0, 2).join('/') || ''}/`} className="hover:text-foreground">Нүүр</a>
          <span className="mx-1.5">/</span>
          <a href={pathname} className="hover:text-foreground">Бүтээгдэхүүн</a>
          {currentCategoryName && (
            <>
              <span className="mx-1.5">/</span>
              <span className="text-foreground">{currentCategoryName}</span>
            </>
          )}
        </nav>
        <div className="flex gap-8 lg:gap-10">
          <aside className="w-[280px] shrink-0 hidden lg:block lg:sticky lg:top-30 lg:self-start lg:max-h-[calc(100vh-8.5rem)] lg:overflow-y-auto lg:overflow-x-hidden lg:overscroll-contain lg:pr-2 lg:pb-6 scrollbar-light">
            {SidebarContent}
          </aside>
          <main className="flex-1 min-w-0">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-5 flex-wrap">
              <div className="flex items-end gap-2">
                {isLoading ? (
                  <div className="text-[15px] text-foreground/70">Уншиж байна...</div>
                ) : (
                  <>
                    <h2 className="text-[20px] font-semibold text-foreground">{displayedProducts.length.toLocaleString('en-US')} бүтээгдэхүүн</h2>
                    {metadata?.total > displayedProducts.length && (
                      <span className="text-[13px] text-foreground/55 mb-[2px]">/ {metadata.total.toLocaleString('en-US')}</span>
                    )}
                  </>
                )}
              </div>
              <div className="flex items-center justify-between lg:justify-end gap-2 w-full lg:w-auto">
                <Button
                  variant="ghost"
                  size="sm"
                  className="lg:hidden hover:bg-[#F1EEE7]!"
                  onClick={() => setOpenFilters(true)}
                  icon={SlidersHorizontal}
                >
                  Шүүлтүүр
                </Button>
                <Menu as="div" className="relative">
                  <MenuButton className="inline-flex items-center gap-2 h-10 px-4 text-[14px] font-medium text-foreground hover:bg-[#F1EEE7] rounded-md cursor-pointer">
                    <span className="hidden lg:inline text-foreground/55">Эрэмбэлэх:</span> {activeSortLabel} <ChevronDown className="size-4" />
                  </MenuButton>
                  <MenuItems anchor="bottom end" className="mt-2 w-64 rounded-xl bg-cream border border-foreground/10 shadow-lg p-1 z-50 focus:outline-none">
                    {SORT_OPTIONS.map((o: any) => (
                      <MenuItem key={o.value}>
                        <button
                          onClick={() => onSortChange(o.value)}
                          className={`w-full text-left px-3 h-10 rounded-lg text-[14px] hover:bg-foreground/5 cursor-pointer ${sort === o.value ? 'font-semibold text-foreground' : 'text-foreground/70'}`}
                        >
                          {o.label}
                        </button>
                      </MenuItem>
                    ))}
                  </MenuItems>
                </Menu>
              </div>
            </div>
            {chips.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-5">
                {chips.map(c => (
                  <button
                    key={c.key}
                    onClick={c.clear}
                    className="inline-flex items-center gap-1.5 h-8 pl-3 pr-2 rounded-full bg-[#F1EEE7] hover:bg-[#E9E4DA] text-foreground text-[13px] cursor-pointer"
                  >
                    <span>{c.label}</span>
                    <X className="size-3.5 text-foreground/55" />
                  </button>
                ))}
                {hasActiveFilters && (
                  <button onClick={clearAll} className="h-8 px-3 text-[13px] font-medium underline text-foreground hover:opacity-70 cursor-pointer">
                    Бүгдийг арилгах
                  </button>
                )}
              </div>
            )}
            {isLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-10">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="aspect-square rounded-2xl bg-foreground/5 animate-pulse" />
                ))}
              </div>
            ) : displayedProducts.length === 0 ? (
              <div className="border border-foreground/10 rounded-xl py-20 text-center">
                <div className="text-foreground font-semibold mb-1">Шүүлтүүрт тохирох бүтээгдэхүүн алга</div>
                <div className="text-foreground/55 text-sm mb-5">Шүүлтүүрээ багасгаад дахин оролдоно уу.</div>
                <button onClick={clearAll} className="h-10 px-5 rounded-md bg-foreground text-white text-sm font-medium hover:bg-foreground/90 cursor-pointer">Шүүлтүүр арилгах</button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-10">
                  {displayedProducts.map((p: any) => (
                    <ProductCard key={p.customId || p.id} product={p} vendor={vendor} />
                  ))}
                  {Array.from({ length: pendingCount }).map((_, i) => (
                    <div key={`pending-${i}`} ref={i === 0 ? loadMoreRef : undefined}>
                      <div className="aspect-square rounded-2xl bg-foreground/5" />
                      <div className="pt-4 flex flex-col gap-2">
                        <div className="h-4 w-1/3 rounded bg-foreground/5" />
                        <div className="h-5 w-3/4 rounded bg-foreground/5" />
                        <div className="h-5 w-1/2 rounded bg-foreground/5" />
                      </div>
                    </div>
                  ))}
                </div>
                <div ref={pendingCount === 0 ? loadMoreRef : null} className="flex items-center justify-center mt-8">
                  {isLoadingMore && <Loader2 className="size-5 animate-spin text-foreground/40" />}
                </div>
              </>
            )}
          </main>
        </div>
      </div>
      <Transition show={openFilters} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={() => setOpenFilters(false)}>
          <TransitionChild as={Fragment} enter="transition-opacity ease-linear duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-linear duration-300" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/25" />
          </TransitionChild>
          <div className="fixed inset-0 z-50 flex">
            <TransitionChild as={Fragment} enter="transition ease-in-out duration-300 transform" enterFrom="-translate-x-full" enterTo="translate-x-0" leave="transition ease-in-out duration-300 transform" leaveFrom="translate-x-0" leaveTo="-translate-x-full">
              <DialogPanel className="relative w-[88%] max-w-sm flex flex-col bg-cream shadow-2xl rounded-r-3xl overflow-hidden">
                <div className="flex items-center justify-between px-5 h-14 border-b border-foreground/5 shrink-0">
                  <span className="text-[15px] font-semibold text-foreground">Шүүлтүүр</span>
                  <button onClick={() => setOpenFilters(false)} aria-label="Хаах" className="w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-[#F1EEE7] cursor-pointer">
                    <X className="size-5" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-3">
                  {SidebarContent}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>
    </section>
  )
}
